/**
 * 设备管理 —— 录入设备档案、批准/停用设备、进入设备详情。
 *
 * 列表数据来自 `GET /api/devices`（分页 + 状态 + 关键字），
 * 变更类操作（录入、批准、停用）成功后统一 `reload()` 重新拉取，
 * 避免在客户端维护一份可能与服务端不一致的副本。
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Download,
  Loader2,
  Plus,
  Search,
  Smartphone,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { api, downloadDevicesCsv, useAsync, type Device, type DeviceStatus } from '@/lib/api'
import { formatTime, relativeTime } from '@/lib/utils'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const PAGE_SIZE = 20

/**
 * 设备类型。当前产品线只做折光仪，所以注册表单只提供这一项。
 * 服务端按字符串存 device_type，历史数据里的其它类型仍会照原样显示与检索，不受影响。
 */
const DEVICE_TYPES = ['折光仪'] as const

const STATUS_META: Record<
  DeviceStatus,
  { label: string; variant: 'warning' | 'success' | 'destructive' | 'outline' }
> = {
  pending: { label: '待批准', variant: 'warning' },
  active: { label: '已激活', variant: 'success' },
  suspended: { label: '已停用', variant: 'destructive' },
  retired: { label: '已退役', variant: 'outline' },
}

/** 状态筛选下拉的哨兵值 —— Radix Select 的 Item 不接受空字符串。 */
const ALL_STATUS = 'all'

interface RegisterForm {
  deviceId: string
  deviceName: string
  deviceType: string
  model: string
  androidId: string
  appVersion: string
  machineCode: string
  publicKeyPem: string
  note: string
}

const EMPTY_FORM: RegisterForm = {
  deviceId: '',
  deviceName: '',
  deviceType: DEVICE_TYPES[0],
  model: '',
  androidId: '',
  appVersion: '',
  machineCode: '',
  publicKeyPem: '',
  note: '',
}

const TEXTAREA_CLASS =
  'flex min-h-[88px] w-full rounded-md border border-[var(--color-input)] bg-transparent px-3 py-2 text-xs leading-relaxed shadow-sm transition-colors placeholder:text-[var(--color-muted-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] disabled:cursor-not-allowed disabled:opacity-50'

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

export function DevicesPage() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<DeviceStatus | typeof ALL_STATUS>(ALL_STATUS)
  const [keyword, setKeyword] = useState('')
  const [appliedKeyword, setAppliedKeyword] = useState('')
  const [exporting, setExporting] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<RegisterForm>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [actingId, setActingId] = useState<string | null>(null)

  const list = useAsync(
    () =>
      api.listDevices({
        page,
        pageSize: PAGE_SIZE,
        ...(statusFilter === ALL_STATUS ? {} : { status: statusFilter }),
        ...(appliedKeyword ? { keyword: appliedKeyword } : {}),
      }),
    [page, statusFilter, appliedKeyword],
  )

  // 关键字防抖：输入停止 350ms 后自动检索（回车可立即检索）。
  useEffect(() => {
    const id = window.setTimeout(() => {
      setAppliedKeyword(keyword.trim())
      setPage(1)
    }, 350)
    return () => window.clearTimeout(id)
  }, [keyword])

  const rows = list.data?.rows ?? []
  const total = list.data?.total ?? 0

  /**
   * 导出设备清单。范围是**当前筛选条件下的全部设备**，不是这一页 ——
   * 与列表共用 statusFilter / appliedKeyword，避免「页面筛出 12 台、导出却是全部」。
   */
  async function exportCsv() {
    setExporting(true)
    try {
      const { blob, filename, rows } = await downloadDevicesCsv({
        status: statusFilter === ALL_STATUS ? undefined : statusFilter,
        keyword: appliedKeyword || undefined,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success(`已导出 ${rows ?? '全部'} 台设备`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '导出失败')
    } finally {
      setExporting(false)
    }
  }
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  function updateForm<K extends keyof RegisterForm>(key: K, value: RegisterForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  // ── 删除设备 ──
  const [pendingDelete, setPendingDelete] = useState<Device | null>(null)
  const [deleting, setDeleting] = useState(false)

  async function confirmDelete() {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      const res = await api.deleteDevice(pendingDelete.device_id)
      const r = res.removed ?? {}
      toast.success(
        `已删除设备 ${pendingDelete.device_id}` +
          (r.secrets || r.activationCodes || r.licenseCodes
            ? `（含设备密钥 ${r.secrets ?? 0} 条、仪器激活码 ${r.activationCodes ?? 0} 条、上位机授权码 ${r.licenseCodes ?? 0} 条）`
            : ''),
      )
      setPendingDelete(null)
      list.reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除设备失败')
    } finally {
      setDeleting(false)
    }
  }

  async function submitRegister(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return

    const deviceId = form.deviceId.trim()
    const deviceName = form.deviceName.trim()
    if (!deviceId || !deviceName) {
      toast.error('设备 ID 与设备名称为必填')
      return
    }

    const model = form.model.trim()
    const androidId = form.androidId.trim()
    const appVersion = form.appVersion.trim()
    const machineCode = form.machineCode.trim()
    const publicKeyPem = form.publicKeyPem.trim()
    const note = form.note.trim()

    setSubmitting(true)
    try {
      const created = await api.registerDevice({
        deviceId,
        deviceName,
        deviceType: form.deviceType,
        ...(model ? { model } : {}),
        ...(androidId ? { androidId } : {}),
        ...(appVersion ? { appVersion } : {}),
        ...(machineCode ? { machineCode } : {}),
        ...(publicKeyPem ? { publicKeyPem } : {}),
        ...(note ? { note } : {}),
      })
      toast.success(`已录入设备 ${created.device_name}，状态为待批准`)
      setDialogOpen(false)
      setForm(EMPTY_FORM)
      setPage(1)
      list.reload()
    } catch (err) {
      // 例如 409：设备 xxx 已注册
      toast.error(errorMessage(err, '录入设备失败'))
    } finally {
      setSubmitting(false)
    }
  }

  async function changeStatus(device: Device, next: DeviceStatus) {
    setActingId(device.device_id)
    try {
      await api.setStatus(device.device_id, next)
      toast.success(
        next === 'active'
          ? `已批准 ${device.device_name}`
          : next === 'suspended'
            ? `已停用 ${device.device_name}`
            : `已更新 ${device.device_name} 状态`,
      )
      list.reload()
    } catch (err) {
      toast.error(errorMessage(err, '状态更新失败'))
    } finally {
      setActingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">设备管理</h1>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
              <Input
                className="pl-9"
                placeholder="搜索设备 ID / 名称 / 单位 / 联系人 / 序列号 / 上位机机器码，回车立即检索"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setAppliedKeyword(keyword.trim())
                    setPage(1)
                  }
                }}
              />
            </div>

            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as DeviceStatus | typeof ALL_STATUS)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="全部状态" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUS}>全部状态</SelectItem>
                {(Object.keys(STATUS_META) as DeviceStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_META[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={exporting}
              title="导出当前筛选条件下的全部设备（不受分页影响）"
            >
              {exporting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Download className="size-4" />
              )}
              导出 CSV
            </Button>

            <div className="ml-auto flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
              {list.loading && list.data ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  刷新中…
                </>
              ) : (
                `共 ${total} 台设备`
              )}
            </div>
          </div>

          {list.error && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[color-mix(in_oklch,var(--color-destructive)_40%,transparent)] bg-[color-mix(in_oklch,var(--color-destructive)_12%,transparent)] px-4 py-3 text-sm text-[var(--color-destructive)]">
              <span>加载设备列表失败：{list.error}</span>
              <Button variant="outline" size="sm" onClick={list.reload}>
                重试
              </Button>
            </div>
          )}

          <div className="rounded-md border border-[var(--color-border)]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>设备名称</TableHead>
                  <TableHead>单位 / 联系人</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>设备密钥</TableHead>
                  <TableHead>最后在线</TableHead>
                  <TableHead>注册时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.loading && !list.data ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="py-10 text-center text-[var(--color-muted-foreground)]"
                    >
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="size-4 animate-spin" />
                        加载中…
                      </span>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-[var(--color-muted-foreground)]">
                        <Smartphone className="size-6" />
                        <div className="text-sm">
                          {appliedKeyword || statusFilter !== ALL_STATUS
                            ? '没有符合条件的设备，试试调整关键字或状态筛选。'
                            : '暂无设备。仪器完成注册并上报后会出现在这里。'}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((device) => {
                    const meta = STATUS_META[device.status]
                    const busy = actingId === device.device_id
                    return (
                      <TableRow key={device.id}>
                        <TableCell>
                          <div className="font-medium">{device.device_name}</div>
                          <div className="font-mono-token text-xs text-[var(--color-muted-foreground)]">
                            {device.device_id}
                          </div>
                        </TableCell>
                        <TableCell>
                          {device.company_name ? (
                            <div className="max-w-[220px]">
                              <div className="truncate" title={device.company_name}>
                                {device.company_name}
                              </div>
                              <div className="truncate text-xs text-[var(--color-muted-foreground)]">
                                {[device.contact_name, device.contact_phone]
                                  .filter(Boolean)
                                  .join(' · ') || '—'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-[var(--color-muted-foreground)]">
                              未上报
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-[var(--color-muted-foreground)]">
                          {device.device_type}
                          {device.model ? <div className="text-xs">{device.model}</div> : null}
                          {device.serial_number ? (
                            <div className="font-mono-token text-xs">{device.serial_number}</div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Badge variant={meta.variant}>{meta.label}</Badge>
                        </TableCell>
                        <TableCell>
                          {device.has_secret ? (
                            <Badge variant="success">已签发</Badge>
                          ) : (
                            <Badge variant="outline">未签发</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-[var(--color-muted-foreground)]">
                          {relativeTime(device.last_seen_at)}
                        </TableCell>
                        <TableCell className="font-mono-token text-xs text-[var(--color-muted-foreground)]">
                          {formatTime(device.registered_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1.5">
                            <Button asChild variant="ghost" size="sm">
                              <Link to={`/devices/${encodeURIComponent(device.device_id)}`}>
                                查看
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[var(--color-destructive)] hover:text-[var(--color-destructive)]"
                              disabled={busy}
                              onClick={() => setPendingDelete(device)}
                              title="删除设备及其设备密钥、仪器激活码与上位机授权码"
                            >
                              <Trash2 className="size-3.5" />
                              删除
                            </Button>
                            {device.status !== 'active' && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={busy}
                                onClick={() => void changeStatus(device, 'active')}
                              >
                                {busy ? (
                                  <Loader2 className="size-3.5 animate-spin" />
                                ) : (
                                  <CircleCheck className="size-3.5" />
                                )}
                                批准
                              </Button>
                            )}
                            {device.status === 'active' && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={busy}
                                onClick={() => void changeStatus(device, 'suspended')}
                              >
                                {busy ? <Loader2 className="size-3.5 animate-spin" /> : null}
                                停用
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[var(--color-muted-foreground)]">
            <span>
              第 {page} / {totalPages} 页，共 {total} 条
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || list.loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="size-4" />
                上一页
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || list.loading}
                onClick={() => setPage((p) => p + 1)}
              >
                下一页
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open && submitting) return
          setDialogOpen(open)
          if (!open) setForm(EMPTY_FORM)
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>录入设备</DialogTitle>
            <DialogDescription>
              新录入的设备状态为「待批准」，需在列表中批准后设备端才能正常使用。
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitRegister} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="deviceId">设备 ID（必填）</Label>
                <Input
                  id="deviceId"
                  value={form.deviceId}
                  onChange={(e) => updateForm('deviceId', e.target.value)}
                  placeholder="如 MP-2024-0001"
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="deviceName">设备名称（必填）</Label>
                <Input
                  id="deviceName"
                  value={form.deviceName}
                  onChange={(e) => updateForm('deviceName', e.target.value)}
                  placeholder="如 一号折光仪"
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="deviceType">设备类型（必填）</Label>
                <Select value={form.deviceType} onValueChange={(v) => updateForm('deviceType', v)}>
                  <SelectTrigger id="deviceType">
                    <SelectValue placeholder="请选择设备类型" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEVICE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="model">型号</Label>
                <Input
                  id="model"
                  value={form.model}
                  onChange={(e) => updateForm('model', e.target.value)}
                  placeholder="选填"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="androidId">Android ID</Label>
                <Input
                  id="androidId"
                  className="font-mono-token text-xs"
                  value={form.androidId}
                  onChange={(e) => updateForm('androidId', e.target.value)}
                  placeholder="选填"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="appVersion">App 版本</Label>
                <Input
                  id="appVersion"
                  value={form.appVersion}
                  onChange={(e) => updateForm('appVersion', e.target.value)}
                  placeholder="如 1.0.0"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="machineCode">上位机机器码</Label>
                <Input
                  id="machineCode"
                  className="font-mono-token text-xs"
                  value={form.machineCode}
                  onChange={(e) => updateForm('machineCode', e.target.value)}
                  placeholder="选填"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="publicKeyPem">设备公钥（PEM）</Label>
              <textarea
                id="publicKeyPem"
                className={`${TEXTAREA_CLASS} font-mono-token`}
                value={form.publicKeyPem}
                onChange={(e) => updateForm('publicKeyPem', e.target.value)}
                placeholder={'-----BEGIN PUBLIC KEY-----\n…\n-----END PUBLIC KEY-----'}
              />
              <p className="text-xs text-[var(--color-muted-foreground)]">
                用于加密下发设备密钥；未填写公钥时无法生成下发信封（可稍后由设备上报或手动补充）。
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="note">备注</Label>
              <Input
                id="note"
                value={form.note}
                onChange={(e) => updateForm('note', e.target.value)}
                placeholder="选填，如安装位置、负责人"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => {
                  setDialogOpen(false)
                  setForm(EMPTY_FORM)
                }}
              >
                取消
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Plus className="size-4" />
                )}
                提交录入
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {pendingDelete ? (
        <ConfirmDialog
          title="删除设备"
          description={
            `将永久删除「${pendingDelete.device_name}」（${pendingDelete.device_id}）及其` +
            '设备密钥、仪器激活码、上位机授权码与阶段锁状态。此操作不可撤销；' +
            '该设备若再次接入需重新登记并重新签发设备密钥。'
          }
          confirmLabel="确认删除"
          danger
          busy={deleting}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}
    </div>
  )
}
